/**
 * SECTOR-PREVIEW — Renderer fiel de UN sector arbitrario para previews en admin.
 *
 * Renderea en un canvas arbitrario un sector de la rueda usando la misma
 * matematica que el wheel real (shared/core/wheel-of-fortune.js). Preserva:
 *   - Arco correcto segun cantidad de sectores del tipo de rueda.
 *   - Radios rIn/rMid/rOut proporcionales al size del canvas.
 *   - Texto rotado tangente al arco (NO horizontal).
 *   - Gradiente metalico del color del sector (verde LOTO para ceros,
 *     rojo/negro para numerales, configurable para ruedas futuras).
 *   - Soporte para sector con ICONO (`grad: path/URL`) — ruedas de animales,
 *     equipos, simbolos.
 *   - Pines dorados en los bordes del sector.
 *   - Reparto vertical fsLbl/fsNum igual que el wheel.
 *
 * NO incluye: flapper, bezel metalico exterior, centro (logos), otros sectores.
 * Enfocado: el admin ve EXACTAMENTE como se ve el sector en la rueda al girar.
 *
 * `drawSectorPreview` acepta CUALQUIER sector (cero o no) con color y grad
 * configurables. Para el sector cero clasico, el caller pasa el verde LOTO
 * via la constante exportada `COLOR_VERDE_LOTO_CANONICO`.
 *
 * Uso (sector cero clasico):
 *   drawSectorPreview(canvas, {
 *       tipoRuleta: 'european',
 *       top: 'LOTO',
 *       bot: '37',
 *       color: '#22bf08',  // opcional, default verde LOTO si parece cero
 *   });
 *
 * Uso (sector no-cero numerico):
 *   drawSectorPreview(canvas, {
 *       tipoRuleta: 'european',
 *       bot: '17',
 *       color: '#c8102e',  // rojo
 *   });
 *
 * Uso (sector con icono, rueda futura):
 *   drawSectorPreview(canvas, {
 *       tipoRuleta: 'animalitos',
 *       top: 'Delfin',
 *       grad: '/assets/sectores/delfin.svg',
 *       color: '#1d4ed8',
 *   });
 */

import { WheelConfig } from '../config/games/lotowheel/wheel-config.js';
import {
    ROULETTE_TYPES,
    normalizarClaveDeTipoDeRueda,
} from '../config/games/lotowheel/roulette-numbers-order-config.js';

const { WHEEL_CONSTANTS, WHEEL_CONFIG } = WheelConfig;

// Color verde LOTO canonico del cero — fallback cuando el caller no declara
// `color`. Espejo del `#22bf08` que viven en las recetas (LOTO/LOTO+/LOTO++).
export const COLOR_VERDE_LOTO_CANONICO = '#22bf08';

// SSOT — sectores y geometria salen de ROULETTE_TYPES. Fail-fast: si el
// caller pasa un tipo invalido (typo, undefined, receta inexistente) el
// componente lanza. Cero "preview de european" silenciosa cuando el caller
// quiso pintar otra cosa.
function _getTipoConfig(tipoRuleta) {
    const claveDeTipo = normalizarClaveDeTipoDeRueda(tipoRuleta);
    const cfg = claveDeTipo ? ROULETTE_TYPES[claveDeTipo] : null;
    if (!cfg) {
        throw new Error(
            `[sector-preview] tipoRuleta requerido y debe existir en las recetas — recibido: ${JSON.stringify(tipoRuleta)}`
        );
    }
    return cfg;
}

// Cache de imagenes ya cargadas (por URL) — drawImage es sincrono, asi que
// si la imagen no esta cargada todavia, no se dibuja y el caller debe
// re-invocar al onload. Patron simple: una entrada por URL.
//
// SUPUESTOS DE CARDINALIDAD: URLs estables (sin query strings dinamicas tipo
// `?v=hash` ni signed URLs por sesion). Hoy el unico caller es el preview
// del admin, ~10 URLs (iconos por sector × pocos tipos). Si en el futuro la
// generalizacion atrae callers que generen URLs dinamicas, agregar tope LRU.
const _cacheDeImagenesPorUrl = new Map();

/**
 * Carga (o devuelve la cache) de una imagen para usar en `drawImage`.
 * Si esta cargando, devuelve `null` y el caller decide si redibuja luego.
 * @param {string} url
 * @returns {HTMLImageElement|null}
 */
function _obtenerImagenCacheada(url) {
    if (!url) return null;
    if (_cacheDeImagenesPorUrl.has(url)) {
        const img = _cacheDeImagenesPorUrl.get(url);
        return img.complete && img.naturalWidth > 0 ? img : null;
    }
    const img = new Image();
    // Si la URL devuelve 404/CORS/red rota, borramos la entrada del cache
    // para que un reintento posterior pueda recargar. Sin esto, una URL
    // rota quedaba zombi: img.complete=true pero naturalWidth=0, y ningun
    // redraw podia rescatarla — el sector se quedaba sin icono permanente.
    img.onerror = () => {
        _cacheDeImagenesPorUrl.delete(url);
    };
    img.src = url;
    _cacheDeImagenesPorUrl.set(url, img);
    return img.complete && img.naturalWidth > 0 ? img : null;
}

/**
 * Renderea en un canvas el sector indicado, con la geometria del tipo de
 * rueda dado. SSOT generalizado de §8.9.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {Object} params
 * @param {string} params.tipoRuleta - id de una receta existente (ej. 'european',
 *   'mini', 'rueda10'...). Obligatorio. Determina el arco. Lanza si invalido.
 * @param {string} [params.top] - linea superior (label chico). Default ''.
 * @param {(string|number)} [params.bot] - linea inferior (numero/texto grande).
 * @param {string} [params.grad] - URL/path a icono SVG/PNG. Si esta, se
 *   dibuja por encima del color, centrado en el sector. Coexiste con top/bot.
 * @param {string} [params.color] - color de fondo del sector. Default verde
 *   LOTO. Acepta cualquier valor CSS (`'#c8102e'`, `'red'`, etc).
 * @param {Function} [params.onImagenCargada] - callback SIN ARGUMENTOS que
 *   se invoca cuando una imagen `grad` termina de cargar. El caller ya
 *   sabe qué canvas redibujar; la URL no se vuelve a pasar para evitar
 *   que un nuevo consumer invente lógica alrededor de ella. Opcional.
 */
export function drawSectorPreview(canvas, params) {
    const { tipoRuleta, top = '', bot = '', grad = null, color = null, onImagenCargada } = params || {};
    const tipoCfg = _getTipoConfig(tipoRuleta);
    const sectors = tipoCfg.order.length;
    const geom = tipoCfg.geometry || WHEEL_CONSTANTS;

    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // ═══════════════════════════════════════════════════════════════════
    // GEOMETRIA — franja superior del wheel (~14% del radio).
    // ═══════════════════════════════════════════════════════════════════
    const FRANJA = 0.14;
    const rOut = H / FRANJA;
    // Fallback de claves: `ROULETTE_TYPES[*].geometry` (recetas, SSOT actual)
    // usa lowercase `rIn/rMid/rOut`; `WHEEL_CONSTANTS` (fallback defensivo si
    // el tipo es desconocido) usa UPPERCASE `R_IN/R_MID/R_OUT`. El nullish
    // coalescing cubre ambos sin asumir convencion.
    const R_OUT = geom.rOut ?? geom.R_OUT;
    const R_MID = geom.rMid ?? geom.R_MID;
    const R_IN = geom.rIn ?? geom.R_IN;
    const r = rOut / R_OUT;
    const rMid = r * R_MID;
    const rIn = r * R_IN;
    const radioCorte = rOut * (1 - FRANJA);
    const rNumberPos = WHEEL_CONSTANTS.R_NUMBER_POS;
    const textRadius = rMid + (rOut - rMid) * rNumberPos;

    const cx = W / 2;
    const cy = rOut;

    const arcRad = (2 * Math.PI) / sectors;
    const startAngle = -Math.PI / 2 - arcRad / 2;
    const endAngle = -Math.PI / 2 + arcRad / 2;

    ctx.save();
    ctx.translate(cx, cy);

    // ═══════════════════════════════════════════════════════════════════
    // 1. SECTOR (banda superior curvada con gradiente)
    // ═══════════════════════════════════════════════════════════════════
    ctx.beginPath();
    ctx.arc(0, 0, rOut, startAngle, endAngle);
    ctx.arc(0, 0, radioCorte, endAngle, startAngle, true);
    ctx.closePath();

    // Color de relleno del sector: el caller manda, sino verde LOTO.
    // Normalizamos antes de comparar para que `'#22BF08'` o `'#22bf08 '`
    // tambien matcheen el atajo bit-exacto del verde LOTO canonico.
    const colorBase = _normalizarColorHexParaComparacion(color || COLOR_VERDE_LOTO_CANONICO);
    const { stopInner, stopMid, stopOuter } = _derivarStopsDeGradienteDesdeColor(colorBase);
    const gradRadial = ctx.createRadialGradient(0, 0, radioCorte, 0, 0, rOut);
    gradRadial.addColorStop(0, stopInner);
    gradRadial.addColorStop(0.55, stopMid);
    gradRadial.addColorStop(1, stopOuter);
    ctx.fillStyle = gradRadial;
    ctx.fill();

    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(0,0,0,0.4)';
    ctx.stroke();

    // ═══════════════════════════════════════════════════════════════════
    // 2. ICONO (sectores con `grad: URL`) — para ruedas no-numericas
    // ═══════════════════════════════════════════════════════════════════
    if (grad) {
        const img = _obtenerImagenCacheada(grad);
        if (img) {
            // Tamaño del icono: ~40% del alto visible del canvas, manteniendo
            // proporcion. Centrado en el sector (apex apunta arriba, -90°).
            const iconoLado = H * 0.4;
            ctx.save();
            ctx.rotate(-Math.PI / 2);
            ctx.translate(textRadius, 0);
            ctx.rotate(Math.PI / 2);
            ctx.drawImage(img, -iconoLado / 2, -iconoLado / 2, iconoLado, iconoLado);
            ctx.restore();
        } else if (typeof onImagenCargada === 'function') {
            // La imagen aun no esta cargada; pedimos al caller que redibuje
            // cuando termine. Una imagen fallida queda aislada — el sector
            // ya esta dibujado con el color, solo falta el icono encima.
            //
            // addEventListener (no asignacion directa a .onload) permite
            // que multiples canvases registren callbacks distintos para la
            // misma URL en vuelo. Sino el primer caller ganaba y los demas
            // quedaban con sector sin icono hasta forzar un redraw externo.
            const img2 = _cacheDeImagenesPorUrl.get(grad);
            if (img2) {
                img2.addEventListener('load', () => onImagenCargada(), { once: true });
            }
        }
    }

    // ═══════════════════════════════════════════════════════════════════
    // 3. TEXTO (top + bot) — misma matemática que el wheel real.
    // ═══════════════════════════════════════════════════════════════════
    const virtualSize = r * 2;
    const fsNum = virtualSize * WHEEL_CONFIG.rendering.numberSize;
    const fsLbl = virtualSize * WHEEL_CONFIG.rendering.labelSize;

    ctx.save();
    const centerAngle = -Math.PI / 2;
    ctx.rotate(centerAngle);
    ctx.translate(textRadius, 0);
    ctx.rotate(Math.PI / 2);

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const hasTop = top && top.length > 0;
    const hasBot = bot != null && String(bot).length > 0;
    const botStr = hasBot ? String(bot) : '';

    // Cuando hay icono (`grad`), el texto se reduce y se posiciona arriba
    // para no pisar el icono — el icono ocupa el centro del sector.
    //
    // Layout adaptativo segun lo que mande el caller:
    //   - `top` solo (ej. "Delfin"): label chico arriba.
    //   - `bot` solo (ej. "17", sector de animalitos numerado): numero
    //     `fsNum` arriba — mas legible que `fsLbl` para numeros.
    //   - Ambos: prioriza `top` (etiqueta semantica del sector).
    if (grad && (hasTop || hasBot)) {
        if (hasTop) {
            ctx.font = `700 ${fsLbl}px Arial, sans-serif`;
            ctx.fillText(top, 0, -fsNum * 1.1);
        } else {
            ctx.font = `700 ${fsNum}px Arial, sans-serif`;
            ctx.fillText(botStr, 0, -fsNum * 0.9);
        }
    } else if (hasTop && hasBot) {
        // Modo clasico cero: top chico arriba, bot grande abajo.
        ctx.font = `700 ${fsLbl}px Arial, sans-serif`;
        ctx.fillText(top, 0, -fsNum * 0.6);
        ctx.font = `700 ${fsNum}px Arial, sans-serif`;
        ctx.fillText(botStr, 0, 0);
    } else if (hasBot) {
        ctx.font = `700 ${fsNum * 1.4}px Arial, sans-serif`;
        ctx.fillText(botStr, 0, 0);
    } else if (hasTop) {
        ctx.font = `700 ${fsNum}px Arial, sans-serif`;
        ctx.fillText(top, 0, 0);
    }
    ctx.restore();

    // ═══════════════════════════════════════════════════════════════════
    // 4. PINES dorados en los bordes del sector.
    // ═══════════════════════════════════════════════════════════════════
    const pinRadius = rOut * 0.97;
    const pinSize = virtualSize * 0.008;
    [startAngle, endAngle].forEach(angle => {
        const px = Math.cos(angle) * pinRadius;
        const py = Math.sin(angle) * pinRadius;
        ctx.beginPath();
        ctx.arc(px, py, pinSize * 1.2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.4)';
        ctx.fill();
        const pinGrad = ctx.createRadialGradient(px - pinSize * 0.3, py - pinSize * 0.3, 0, px, py, pinSize);
        pinGrad.addColorStop(0, '#fff3b0');
        pinGrad.addColorStop(0.5, '#d4a017');
        pinGrad.addColorStop(1, '#8b6914');
        ctx.beginPath();
        ctx.arc(px, py, pinSize, 0, Math.PI * 2);
        ctx.fillStyle = pinGrad;
        ctx.fill();
    });

    ctx.restore();
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers privados
// ─────────────────────────────────────────────────────────────────────────

/**
 * Dado un color CSS base, deriva los 3 stops del gradiente radial del
 * sector. Los nombres ESPEJAN la posicion radial del stop en el gradiente:
 *
 *   - `stopInner`  → addColorStop(0,    ...)   borde interior (cerca del centro virtual)
 *   - `stopMid`    → addColorStop(0.55, ...)   franja media (mas brillante del sector)
 *   - `stopOuter`  → addColorStop(1,    ...)   borde exterior (mas oscuro)
 *
 * Esta convencion da el look metalico LOTO: la franja media es la mas
 * brillante (color base), bordes son variantes oscuras. Mismo patron que
 * el gradiente del wheel real para cualquier color.
 *
 * Para el verde LOTO canonico devolvemos los stops literales del renderer
 * historico — el preview del cero queda BIT-IDENTICO (anti-regresion
 * visual confirmada empiricamente). Para otros colores se derivan por
 * ajuste de luminancia.
 *
 * @param {string} colorBase - color CSS valido (preferentemente hex 6).
 * @returns {{ stopInner: string, stopMid: string, stopOuter: string }}
 */
function _derivarStopsDeGradienteDesdeColor(colorBase) {
    if (colorBase === COLOR_VERDE_LOTO_CANONICO) {
        return {
            stopInner: '#1e8f06',
            stopMid: '#22bf08',
            stopOuter: '#186b05',
        };
    }
    // Derivacion para colores arbitrarios. Inner ligeramente oscuro
    // respecto al base, mid es el color base (mas brillante), outer mas
    // oscuro. Para colores muy claros (blanco) o muy oscuros (negro) los
    // stops podrian solaparse y el look queda plano — NO rompe.
    return {
        stopInner: _ajustarLuminanciaHexParaSombra(colorBase, -0.15),
        stopMid: colorBase,
        stopOuter: _ajustarLuminanciaHexParaSombra(colorBase, -0.3),
    };
}

/**
 * Normaliza un color CSS para que la comparacion con `COLOR_VERDE_LOTO_
 * CANONICO` matchee aunque venga con espacios o mayusculas. Solo aplica
 * para hex 6 — otros formatos pasan tal cual (no rompe el atajo, solo
 * pierde la bit-exactitud).
 */
function _normalizarColorHexParaComparacion(color) {
    if (typeof color !== 'string') return color;
    const trimmed = color.trim();
    return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed.toLowerCase() : trimmed;
}

/**
 * Ajusta la luminancia de un color hex (`'#rrggbb'`) por un factor.
 * factor > 0 aclara, factor < 0 oscurece. Si el input no es hex valido,
 * devuelve el color tal cual (defensa contra `'red'`, `'rgb(...)'`, etc).
 *
 * @param {string} hex
 * @param {number} factor
 * @returns {string}
 */
// Exportada con prefijo `_` para indicar "uso interno / testing". El
// hallazgo #9 del code-review §8.9 pedia evitar que los tests copien la
// formula y diverjan silenciosamente del modulo. Sin export, los tests
// reimplementaban la formula y un cambio aqui no fallaba el test.
export function _ajustarLuminanciaHexParaSombra(hex, factor) {
    if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const ajustar = c => {
        const ajustado = factor > 0 ? c + (255 - c) * factor : c * (1 + factor);
        return Math.max(0, Math.min(255, Math.round(ajustado)));
    };
    const rA = ajustar(r);
    const gA = ajustar(g);
    const bA = ajustar(b);
    return `#${rA.toString(16).padStart(2, '0')}${gA.toString(16).padStart(2, '0')}${bA.toString(16).padStart(2, '0')}`;
}
